import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";

const GRI403_6 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    voluntaryHealthPromotion: {
      voluntaryHealthPromotionPrograms: "",
      healthInformationConfidentialityMeasures: "",
      healthInfoPrivacyNonDiscrimination: "",
    },

    workerHealthcareAccess: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);

        const data = response.data.data.GRI400?.promotionWorkerHealth || {};
        setFormData({
          voluntaryHealthPromotion: {
            voluntaryHealthPromotionPrograms:
              data.voluntaryHealthPromotion?.voluntaryHealthPromotionPrograms ||
              "",
            healthInfoPrivacyNonDiscrimination:
              data.voluntaryHealthPromotion
                ?.healthInfoPrivacyNonDiscrimination || "",
            healthInformationConfidentialityMeasures:
              data.voluntaryHealthPromotion
                ?.healthInformationConfidentialityMeasures || "",
          },
          workerHealthcareAccess: data.workerHealthcareAccess || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.voluntaryHealthPromotion.voluntaryHealthPromotionPrograms) {
      newErrors.voluntaryHealthPromotionPrograms = "This field is required";
      formIsValid = false;
    }

    if (
      !formData.voluntaryHealthPromotion
        .healthInformationConfidentialityMeasures
    ) {
      newErrors.healthInformationConfidentialityMeasures =
        "This field is required";
      formIsValid = false;
    }

    if (!formData.voluntaryHealthPromotion.healthInfoPrivacyNonDiscrimination) {
      newErrors.healthInfoPrivacyNonDiscrimination = "This field is required";
      formIsValid = false;
    }

    if (!formData.workerHealthcareAccess) {
      newErrors.workerHealthcareAccess = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;
      const payload = {
        type: "GRI400",
        promotionWorkerHealth: {
          ...formData,
        },
        submitted403_6: true,
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/403-7/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/403-7/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}
      {!hide && (
        <p className={styles.section_heading}>
          403 : Occupational Health and Safety
        </p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        403-6 : Promotion of worker health
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}
        <SectionHeading text="a. How does the organization enable workers to access non-occupational medical and healthcare services? Please provide details regarding the extent of access provided." />
        <CustomTextField
          value={formData.workerHealthcareAccess}
          onChange={(e) =>
            handleInputChange("workerHealthcareAccess", e.target.value)
          }
          error={errors.workerHealthcareAccess}
          helperText={errors.workerHealthcareAccess}
          disabled={disabled}
        />

        {/* Section B */}
        <SectionHeading text="b. Describe any voluntary health promotion services and programs offered to workers aimed at mitigating major non-work-related health risks? Additionally, please specify the particular health risks targeted and explain how the organization facilitates workers' access to these services and programs." />
        <CustomTextField
          value={
            formData.voluntaryHealthPromotion.voluntaryHealthPromotionPrograms
          }
          onChange={(e) =>
            handleNestedInputChange(
              "voluntaryHealthPromotion",
              "voluntaryHealthPromotionPrograms",
              e.target.value
            )
          }
          error={errors.voluntaryHealthPromotionPrograms}
          helperText={errors.voluntaryHealthPromotionPrograms}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. How does the organization maintains the confidentiality of workers’ personal health related information?" />
          <CustomTextField
            value={
              formData.voluntaryHealthPromotion
                .healthInformationConfidentialityMeasures
            }
            onChange={(e) =>
              handleNestedInputChange(
                "voluntaryHealthPromotion",
                "healthInformationConfidentialityMeasures",
                e.target.value
              )
            }
            error={errors.healthInformationConfidentialityMeasures}
            helperText={errors.healthInformationConfidentialityMeasures}
            disabled={disabled}
          />

          <SectionHeading text="ii. How does the organization ensures that workers’ personal health-related information and their participation in any services or programs is not used for any favourable or unfavourable treatment of workers. " />
          <CustomTextField
            value={
              formData.voluntaryHealthPromotion
                .healthInfoPrivacyNonDiscrimination
            }
            // onChange={(e) => handleNestedInputChange("healthInfoPrivacyNonDiscrimination", e.target.value)}
            onChange={(e) =>
              handleNestedInputChange(
                "voluntaryHealthPromotion",
                "healthInfoPrivacyNonDiscrimination",
                e.target.value
              )
            }
            error={errors.healthInfoPrivacyNonDiscrimination}
            helperText={errors.healthInfoPrivacyNonDiscrimination}
            disabled={disabled}
          />
        </Box>

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI403_6;
